<script lang="ts">
import { defineComponent } from 'vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { Transaction } from '@/models/accounting/Transaction'
import RegisterForm from '@/views/accounting/registers/RegisterForm.vue'
import { CellClickedEvent, ColDef, GridOptions } from 'ag-grid-community'
import { Register, RegisterStatus } from '@/models/company/Register'
import { AgGridVue } from 'ag-grid-vue3'
import { useI18n } from 'vue-i18n'
import { formatMoney } from '../../../utils/money'
import { formatPostgresDate } from '@/utils/date'
import User from '@/models/user/User'
import HorizontalButton from '@/components/HorizontalButton.vue'

interface RegisterClosingResponse {
  id: string
  externalID: string
  closingDate: string | null
  closedBy: User
  openingDate: string | null
  openedBy: User
}

export default defineComponent({
  name: 'Register',
  components: { RegisterForm, AlertBadge, LoadingSpinner, AgGridVue, HorizontalButton },

  setup() {
    const messages = {
      en: {
        in: 'In',
        out: 'Out',
        totalInRegister: 'Total In Register',
        ePayment: 'e-payments',
        closingReports: 'Register Closing Reports',
        no: 'Register Name',
        status: 'Status',
        openingFloating: 'Opening Floating',
        closingFloating: 'Closing Floating',
        totalCashIn: 'Total Cash In',
        totalCashOut: 'Total Cash Out',
        balance: 'Balance',
        foreignTotalCashIn: '(Foreign Currency) Total Cash In',
        foreignTotalCashOut: '(Foreign Currency) Total Cash Out',
        foreignBalance: '(Foreign Currency) Balance',
        moneyDropped: 'Money Dropped',
        openRegister: 'Open Register',
        closeRegister: 'Close Register',
        action: 'Action',
        open: 'open',
        close: 'closed',
        openedBy: 'Opened By',
        closedBy: 'Closed By',
        closingDate: 'Closing date',
        openingDate: 'Opening date',
        closingTime: 'Closing time',
        openingTime: 'Opening time',
        showMore: 'Show more',
        showLess: 'Show less',
        epaymentIn: 'Total E-Payment In',
        epaymentOut: 'Total E-Payment Out',
        epaymentBalance: 'E-Payment Balance'
      },
      ar: {
        in: 'داخل',
        out: 'خارج',
        totalInRegister: 'اجمالي الموجود في الصندوق',
        ePayment: 'e-payments',
        closingReports: 'تقارير اغلاق الصندوق',
        no: 'اسم المسجلة',
        status: 'الحالة',
        openingFloating: 'المبلغ الدوار عند الفتح',
        closingFloating: 'المبلغ الدوار عند الإغلاق',
        totalCashIn: 'النقد الداخل',
        totalCashOut: 'النقد الخارج',
        balance: 'الرصيد',
        foreignTotalCashIn: 'النقد الداخل (الاجنبي)',
        foreignTotalCashOut: 'النقد الخارج (الاجنبي)',
        foreignBalance: 'الرصيد (الاجنبي)',
        moneyDropped: 'النقد المودّع',
        action: 'فتح/اغلاق',
        open: 'مفتوح',
        close: 'مغلق',
        openedBy: 'موظف الفتح',
        closedBy: 'موظف الاغلاق',
        closingDate: 'تاريخ الاغلاق',
        openingDate: 'تاريخ الفتح',
        closingTime: 'وقت الاغلاق',
        openingTime: 'وقت الفتح',
        openRegister: 'أفتح الصندوق',
        closeRegister: 'أغلق الصندوق',
        showMore: 'المزيد',
        showLess: 'نافذة اصغر',
        epaymentIn: 'الدفع الالكتروني الداخل',
        epaymentOut: 'الدفع الالكتروني الخارج',
        epaymentBalance: 'رصيد النقد الالكتروني'
      }
    }
    const { t } = useI18n({ messages, useScope: 'global' })

    return { t, formatPostgresDate }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: RegisterClosingResponse[] = []
    const gridOptions: GridOptions = {
      autoSizeStrategy: {
        type: 'fitCellContents'
      }
    }
    const defaultColDef: ColDef = {}
    const registerStatus: RegisterStatus = {}

    return {
      loading: false,
      error: {
        title: '',
        body: ''
      },
      showRegisterForm: false,
      initRegisterTransaction: new Transaction(),
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      registerStatus,
      showMore: false
    }
  },

  computed: {
    registerName(): string {
      const r = this.registerStatus?.register
      return (this.$ctx.locale === 'ar' ? r?.arName || r?.enName : r?.enName || r?.arName) || ''
    }
  },

  methods: {
    formatMoney,
    async submitTransaction(registerTransaction: Transaction, cashierPin: string) {
      try {
        this.loading = true
        const url = `${Transaction.ENDPOINT}`
        await this.$http.post<Transaction>(url, registerTransaction, {
          headers: { pin: cashierPin }
        })
        this.loading = false
      } catch (err) {
        this.error.title = err.title
        this.error.body = err.body
        this.loading = false
        return err
      }

      return false
    },

    async submitExchangeTransaction(exchangeTransaction: any, cashierPin: string) {
      try {
        this.loading = true
        const url = `${Transaction.ENDPOINT}/exchange`
        await this.$http.post<Transaction[]>(url, exchangeTransaction, {
          headers: { pin: cashierPin }
        })
        this.loading = false
      } catch (err) {
        this.error.title = err.title
        this.error.body = err.body
        this.loading = false
        return err
      }

      return false
    },

    async updateRowData() {
      this.gridApi?.showLoadingOverlay()
      try {
        const resp = await this.$http.get<RegisterClosingResponse[]>(
          `/company/registers/register-closing?register_id=${this.$route.params.id}`
        )
        resp.forEach((r) => (r.closedBy = User.from(r.closedBy)))
        this.rowData = resp
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      await this.updateRowData()
    },

    async handleRegisterClick(event: any) {
      this.$router.push(
        await this.$router.push(
          this.$Route.SALES_REGISTERS_CLOSE_REGISTER_REPORT.replace(':id', event.data.id)
        )
      )
    }
  },

  async beforeMount() {
    // Get register status
    try {
      this.loading = true
      const registerRes = await this.$http.get<RegisterStatus>(
        `/company/registers/status?register_id=${this.$route.params.id}`
      )
      this.registerStatus = RegisterStatus.from(registerRes)
      this.showRegisterForm =
        this.$ctx.currentRegister.id === this.$route.params.id &&
        this.registerStatus.status === 'open'
    } catch (error) {
      this.$alertModal.showDanger({
        title: error.title,
        body: error.body
      })
    } finally {
      this.loading = false
    }

    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        headerName: this.t('openingDate'),
        field: 'openingDate',
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => {
          return `<div dir="ltr">${formatPostgresDate(params.value, 'YYYY-MM-DD')}</div>`
        }
      },
      {
        headerName: this.t('openingTime'),
        field: 'openingDate',
        cellRenderer: (params) => {
          return `<div dir="ltr">${formatPostgresDate(params.value, 'hh:mm:ss A')}</div>`
        }
      },
      {
        headerName: this.t('closingDate'),
        field: 'closingDate',
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => {
          return `<div dir="ltr">${formatPostgresDate(params.value, 'YYYY-MM-DD')}</div>`
        }
      },
      {
        headerName: this.t('closingTime'),
        field: 'closingDate',
        cellRenderer: (params) => {
          return `<div dir="ltr">${formatPostgresDate(params.value, 'hh:mm:ss A')}</div>`
        }
      }
    ]
  },

  mounted() {
    this.gridApi?.sizeColumnsToFit()
  }
})
</script>

<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge v-else-if="error.title" isDanger :title="error.title" :body="error.body" @dismissed="
    () => {
      error.title = ''
      error.body = ''
    }
  " />

  <template v-else>
    <div v-if="registerName" class="flex items-center gap-2 px-6 pt-2 pb-4 text-lg font-bold text-black" :title="registerName">
      <font-awesome-icon icon="cash-register" class="flex-shrink-0" />
      <span class="truncate">{{ registerName }}</span>
    </div>
    <div class="rtl:pl-5 ltr:pr-5 register-status-grid grid grid-cols-12 gap-1 text-lg text-gray-500">
      <div class="col-span-8 grid gap-x-4 gap-y-1 grid-cols-4">
        <!-- 1st row -->
        <div></div>
        <div class="p-2">{{ t('in') }}</div>
        <div class="p-2">{{ t('out') }}</div>
        <div class="p-2">{{ t('balance') }}</div>

        <!-- 2nd row -->
        <div class="p-2 text-end">{{ t('IQD') }}</div>
        <div class="p-2 text-black bg-[#DCFCE7] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalCashInAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div class="p-2 text-black bg-[#FEE2E2] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalCashOutAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div class="p-2 text-black bg-[#DBEAFE] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalCashInAmount -
            registerStatus?.totalCashOutAmount, 'IQD').replace('iqd', '') }}
        </div>

        <!-- 3rd row -->
        <div class="p-2 text-end">{{ t('USD') }}</div>
        <div class="p-2 text-black bg-[#DCFCE7] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.foreignCurrencyTotalCashInAmount,
            'USD').replace('$', '') }}
        </div>
        <div class="p-2 text-black bg-[#FEE2E2] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.foreignCurrencyTotalCashOutAmount,
            'USD').replace('$', '') }}
        </div>
        <div class="p-2 text-black bg-[#DBEAFE] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.foreignCurrencyTotalCashInAmount -
            registerStatus?.foreignCurrencyTotalCashOutAmount, 'USD').replace('$', '') }}
        </div>

        <!-- 4th row -->
        <div class="p-2 text-end">{{ t('ePayment') }}</div>
        <div class="p-2 text-black bg-[#DCFCE7] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalEPaymentInAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div class="p-2 text-black bg-[#FEE2E2] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalEPaymentOutAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div class="p-2 text-black bg-[#DBEAFE] font-bold rounded-md">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalEPaymentInAmount -
            registerStatus?.totalEPaymentOutAmount, 'IQD').replace('iqd', '') }}
        </div>
      </div>
      <div class="p-2 col-span-4">
        <div>{{ t('totalInRegister') }}</div>
        <div class="grid grid-cols-2 text-black h-full pt-3 pb-5 gap-2">
          <div class="bg-white flex flex-col items-center justify-center rounded-md gap-4">
            <div class="text-2xl uppercase">IQD</div>
            <div class="text-2xl font-bold">
              {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.totalCashInAmount -
                registerStatus?.totalCashOutAmount + registerStatus?.openingFloatingAmount, 'IQD').replace('iqd', '') }}
            </div>
          </div>
          <div class="bg-white flex flex-col items-center justify-center rounded-md gap-4">
            <div class="text-2xl uppercase">USD</div>
            <div class="text-2xl font-bold">
              {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.foreignCurrencyTotalCashInAmount
                - registerStatus?.foreignCurrencyTotalCashOutAmount, 'USD').replace('$', '') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="rtl:pl-5 ltr:pr-5 border-dashed border-t-2 mt-2"></div>
    <div class="rtl:pl-5 ltr:pr-5 grid grid-cols-12 gap-1 text-lg text-gray-500 mt-2">
      <div class="col-span-8 grid gap-x-4 grid-cols-4 gap-2">
        <!-- opening float -->
        <div class="p-2 text-end">{{ t('openingFloating') }}</div>
        <div class="p-2 text-black bg-[#FFF9E4] font-bold rounded-md max-h-[45px]">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.openingFloatingAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div></div>
        <div class="p-2 text-end">{{ t('openedBy') }}</div>

        <!-- closing float -->
        <div class="p-2 text-end">{{ t('closingFloating') }}</div>
        <div class="p-2 text-black bg-[#FFF9E4] font-bold rounded-md max-h-[45px]">
          {{ registerStatus?.status === 'close' ? '-' : formatMoney(registerStatus?.closingFloatingAmount,
            'IQD').replace('iqd', '') }}
        </div>
        <div></div>
        <div class="p-2 text-end rtl:mt-4">{{ t('closedBy') }}</div>
      </div>
      <div class="px-2 col-span-4 grid grid-cols-1 gap-2">
        <div class="p-2 bg-[#DADADA] font-bold rounded-md flex justify-between h-[45px]">
          <div class="text-gray-800 font-bold">{{ registerStatus?.status === 'close' ? '-' :
            registerStatus?.openedBy.fullName }}</div>
          <div class="text-gray-700" dir="ltr">
            {{ registerStatus?.status === 'close' ? '' : (registerStatus?.openedAt ?
              formatPostgresDate(registerStatus?.openedAt, 'hh:mm A') : '') }}
          </div>
        </div>
        <div class="p-2 bg-[#DADADA] font-bold rounded-md flex justify-between h-[45px]">
          <div class="text-gray-800 font-bold">{{ registerStatus?.closedBy?.fullName }}</div>
          <div class="text-gray-700" dir="ltr">
            {{
              registerStatus?.closedAt
                ? formatPostgresDate(registerStatus?.closedAt, 'hh:mm A')
                : ''
            }}
          </div>
        </div>
      </div>
    </div>
    <div class="rtl:pl-5 ltr:pr-5 border-dashed border-t-2 mt-2"></div>
    <div class="rtl:pl-5 ltr:pr-5 grid grid-cols-12 gap-1 text-lg text-gray-500 mt-2">
      <div class="col-span-8 grid gap-x-4 grid-cols-4">
        <div class="p-2 text-end">{{ t('status') }}</div>
        <div :class="{
          'bg-[#FEE2E2]': registerStatus?.status == 'close',
          'bg-[#DCFCE7]': registerStatus?.status == 'open'
        }" class="p-2 text-black rounded-md text-xl uppercase tracking-[2px] max-h-[45px]">
          {{ t(registerStatus.status) }}
        </div>
        <div class="col-span-2"></div>
      </div>
      <div class="p-2 col-span-4 grid grid-cols-2">
        <div></div>
        <horizontal-button v-if="
          $ctx.currentRegister.id == registerStatus?.register.id &&
          registerStatus?.status == 'close'
        " :title="t('openRegister')" faIcon="cash-register" @click.prevent="
            () => {
              $router.push($Route.SALES_REGISTERS_OPEN_REGISTER)
            }
          " />
        <horizontal-button v-if="
          $ctx.currentRegister.id == registerStatus?.register.id &&
          registerStatus?.status == 'open'
        " :title="t('closeRegister')" faIcon="cash-register" is-danger @click.prevent="
            () => {
              $router.push($Route.SALES_REGISTERS_CLOSE_REGISTER)
            }
          " />
      </div>
    </div>
    <div class="rtl:pl-5 ltr:pr-5 border-t-2 mt-4"></div>
    <section class="w-full" id="stats" v-if="registerStatus && 0">
      <div class="bg-gray-100 px-5 pt-5">
        <div class="flex flex-col md:flex-row sm:flex-row justify-around text-gray-800">
          <div
            class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('openingFloating') }}</h3>
            <p class="text-3xl mt-2 mb-1">
              {{ formatMoney(registerStatus?.openingFloatingAmount, registerStatus.currency) }}
            </p>
          </div>

          <div
            class="bg-green-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('totalCashIn') }}</h3>
            <p class="text-3xl mt-2 mb-1">
              {{ formatMoney(registerStatus?.totalCashInAmount, registerStatus.currency) }}
            </p>
          </div>

          <div
            class="bg-red-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('totalCashOut') }}</h3>
            <p class="text-3xl mt-2 mb-1">
              {{ formatMoney(registerStatus?.totalCashOutAmount, registerStatus.currency) }}
            </p>
          </div>

          <div
            class="bg-blue-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('balance') }}</h3>
            <p class="text-3xl mt-2 mb-1">
              {{ formatMoney(registerStatus?.balanceAmount, registerStatus.currency) }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-gray-100 px-5 pt-2">
        <div class="flex flex-col md:flex-row sm:flex-row justify-around text-gray-800">
          <div class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center"
            :class="{ 'opacity-30': registerStatus.status === 'open' }">
            <h3 class="text-sm">{{ t('closingFloating') }}</h3>
            <p class="text-3xl mt-2 mb-1" v-if="registerStatus?.status === 'close'">
              {{ formatMoney(registerStatus?.closingFloatingAmount, registerStatus.currency) }}
            </p>
          </div>

          <div
            class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('openedBy') }}</h3>
            <p class="text-3xl mt-2 mb-1">{{ registerStatus?.openedBy.fullName }}</p>
          </div>

          <div class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center"
            :class="{ 'opacity-30': registerStatus.status === 'open' }">
            <h3 class="text-sm">{{ t('closedBy') }}</h3>
            <p class="text-3xl mt-2 mb-1" v-if="registerStatus.status === 'close'">
              {{ registerStatus?.closedBy.fullName }}
            </p>
          </div>

          <div
            class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
            <h3 class="text-sm">{{ t('status') }}</h3>
            <p class="text-3xl mt-2 mb-1">{{ t(registerStatus?.status) }}</p>
          </div>
        </div>
      </div>

      <div v-if="showMore" class="mb-5">
        <div class="bg-gray-100 px-5 pt-2">
          <div class="flex flex-col md:flex-row sm:flex-row justify-around text-gray-800">
            <div
              class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center opacity-30">
            </div>

            <div
              class="bg-green-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('foreignTotalCashIn') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{
                  formatMoney(
                    registerStatus.foreignCurrencyTotalCashInAmount,
                    registerStatus.foreignCurrency
                  )
                }}
              </p>
            </div>

            <div
              class="bg-red-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('foreignTotalCashOut') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{
                  formatMoney(
                    registerStatus.foreignCurrencyTotalCashOutAmount,
                    registerStatus.foreignCurrency
                  )
                }}
              </p>
            </div>

            <div
              class="bg-blue-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('foreignBalance') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{
                  formatMoney(
                    registerStatus.foreignCurrencyBalanceAmount,
                    registerStatus.foreignCurrency
                  )
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="bg-gray-100 px-5 pt-2">
          <div class="flex flex-col md:flex-row sm:flex-row justify-around text-gray-800">
            <div
              class="bg-white p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center opacity-30">
            </div>

            <div
              class="bg-green-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('epaymentIn') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{ formatMoney(registerStatus.totalEPaymentInAmount, registerStatus.currency) }}
              </p>
            </div>

            <div
              class="bg-red-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('epaymentOut') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{ formatMoney(registerStatus.totalEPaymentOutAmount, registerStatus.currency) }}
              </p>
            </div>

            <div
              class="bg-blue-100 p-4 lg:w-2/6 rounded-lg shadow-md mb-4 sm:mb-0 sm:mx-2 flex flex-col items-center justify-center">
              <h3 class="text-sm">{{ t('epaymentBalance') }}</h3>
              <p class="text-3xl mt-2 mb-1">
                {{ formatMoney(registerStatus.ePaymentBalanceAmount, registerStatus.currency) }}
              </p>
            </div>
          </div>
        </div>

        <div class="mb-5 px-7 pt-2 flex justify-end">
          <button @click="showMore = false" class="text-blue-700 hover:text-blue-500">
            {{ t('showLess') }}
          </button>
        </div>
      </div>

      <div v-else class="mb-5 px-7 pt-2 flex justify-end">
        <button @click="showMore = true" class="text-blue-700 hover:text-blue-500">
          {{ t('showMore') }}
        </button>
      </div>
    </section>

    <section id="actions" class="flex flex-col md:flex-row justify-around mt-2">
      <div class="w-10/12 md:w-7/12 mx-6">
        <div class="mb-6">
          <h1>{{ t('closingReports') }}</h1>
        </div>
        <ag-grid-vue class="ag-theme-alpine mt-4 md:mt-0" :columnDefs="columnDefs" :rowData="rowData"
          rowSelection="multiple" :enable-rtl="$ctx.getDir() === 'rtl'" style="height: 45vh" :pagination="true"
          :paginationAutoPageSize="true" :defaultColDef="defaultColDef" :onGridReady="onGridReady"
          @cell-double-clicked="handleRegisterClick">
        </ag-grid-vue>
      </div>
      <div v-if="showRegisterForm" class="w-full md:w-5/12 flex justify-center">
        <register-form :initRegisterTransaction="initRegisterTransaction" :successMsg="$t('actions.saveChangesSuccess')"
          :submitAction="submitTransaction" :submitExchangeAction="submitExchangeTransaction" :clearFormOnSubmit="true"
          :enableClearForm="true" class="w-10/12" />
      </div>
    </section>
  </template>
</template>
<style></style>
